'use client';

import { useTranslation } from 'react-i18next';
import { Alert, Button, Grid, Snackbar, TextField } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useState } from 'react';
import { z } from 'zod';
import { ACCOUNT_NUMBER_REGEX, CARD_NUMBER_REGEX, IBAN_REGEX, PRICE_REGEX } from '../../../constants/regex.ts';
import { useForm, SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import IResponse from '../../../interfaces/response.ts';
import axios from 'axios';
import { SettlementsDataGrid } from './SettlementsDataGrid.tsx';

const settlementSchema = z.object({
	account_number: z
		.string()
		.optional()
		.refine(
			val => {
				if (val === undefined || val === '') return true;
				return ACCOUNT_NUMBER_REGEX.test(val);
			},
			{ message: 'invalid_account_number_message' },
		),
	sheba_number: z
		.string()
		.optional()
		.refine(
			val => {
				if (val === undefined || val === '') return true;
				return IBAN_REGEX.test(val);
			},
			{ message: 'invalid_iban_message' },
		),
	card_number: z
		.string()
		.optional()
		.refine(
			val => {
				if (val === undefined || val === '') return true;
				return CARD_NUMBER_REGEX.test(val);
			},
			{ message: 'invalid_card_number_message' },
		),
	amount: z.string().regex(PRICE_REGEX, {
		message: 'invalid_price_message',
	}),
});

type SettlementFormData = z.infer<typeof settlementSchema>;

const Settlement: React.FC = () => {
	const [submitting, setSubmitting] = useState(false);
	const [open, setOpen] = useState(false);

	const { t } = useTranslation();

	const {
		register,
		handleSubmit,
		formState: { errors },
		reset,
	} = useForm<SettlementFormData>({
		resolver: zodResolver(settlementSchema),
	});

	const handleWithdraw: SubmitHandler<SettlementFormData> = data => {
		if (data.sheba_number && data.sheba_number.startsWith('IR')) {
			data.sheba_number = data.sheba_number.substring(2);
		}
		if ((data.account_number && data?.account_number?.length > 0) || (data.card_number && data.card_number?.length > 0) || (data.sheba_number && data.sheba_number?.length > 0)) {
			setSubmitting(true);
			axios<IResponse<boolean>>({
				url: '/accounting-profile/create-settlement',
				method: 'POST',
				data: data,
			})
				.then(res => {
					if (res.data.success) {
						reset();
					}
				})
				.finally(() => {
					setSubmitting(false);
				});
		} else {
			setOpen(true);
		}
	};

	const handleCancel = () => {
		reset();
	};
	return (
		<>
			<Snackbar open={open} autoHideDuration={6000}>
				<Alert severity="error" variant="filled" sx={{ width: '100%' }}>
					وارد کردن شماره کارت اجباری است
				</Alert>
			</Snackbar>
			<div className="flex h-full w-full flex-col items-center justify-center">
				<div className="flex w-full flex-col gap-6 rounded-lg bg-white">
					<form className="w-full rounded p-6 text-base" onSubmit={handleSubmit(handleWithdraw)}>
						<Grid container spacing={2}>
							<Grid item xs={12} sm={6}>
								<TextField fullWidth variant="outlined" label={t('account_number')} {...register('account_number')} error={!!errors.account_number} helperText={t(errors.account_number?.message || '')} />
							</Grid>
							<Grid item xs={12} sm={6}>
								<TextField fullWidth variant="outlined" label={t('sheba_number')} {...register('sheba_number')} error={!!errors.sheba_number} helperText={t(errors.sheba_number?.message || '')} />
							</Grid>
							<Grid item xs={12} sm={6}>
								<TextField fullWidth variant="outlined" label={t('card_number')} {...register('card_number')} error={!!errors.card_number} helperText={t(errors.card_number?.message || '')} />
							</Grid>
							<Grid item xs={12} sm={6}>
								<TextField fullWidth variant="outlined" label={t('amount')} {...register('amount')} error={!!errors.amount} helperText={t(errors.amount?.message || '')} />
							</Grid>
						</Grid>
						<div className="me-4 mt-5 flex flex-row-reverse gap-3">
							<Button variant="outlined" className="my-2 w-28 border-gray-400 text-gray-400" onClick={handleCancel}>
								{t('cancel')}
							</Button>
							<LoadingButton variant="contained" className="my-2 w-60 bg-primary-main text-white" type="submit" loading={submitting}>
								{submitting ? '' : t('withdraw')}
							</LoadingButton>
						</div>
					</form>
				</div>

				<div className="mt-2 w-full">
					<SettlementsDataGrid />
				</div>
			</div>
		</>
	);
};

export default Settlement;
